import { parseISO, parse, format } from 'date-fns';
import { deepCamelizeKeys } from './v1_adapter';

/**
 * SCHEMA_MAPPING: Single source of truth for API ↔ Form state transformations
 *
 * This module centralizes complex field transformations between the Rails API
 * and the React form state. Simple 1:1 field mappings (snake_case ↔ camelCase)
 * are handled automatically via deepCamelizeKeys/deepSnakeKeys utilities.
 *
 * Structure:
 * - COMPUTED_FIELDS: Complex transformations with custom logic
 * - Helper functions for serialize/deserialize operations
 */

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Parse ISO string as UTC if no timezone info present
 * Handles multiple ISO 8601 formats:
 * - "2026-03-26T15:26:25+00:00" (PostgREST format with timezone)
 * - "2026-03-26T15:26:25.000Z" (JavaScript .toISOString() format)
 * - "2026-03-26T15:26:25" (no timezone - assumes UTC)
 * - "2026-03-26T15:26:25-05:00" (negative timezone offset)
 *
 * @param {string} isoString - ISO 8601 timestamp
 * @returns {Date} Parsed date object
 */
function parseIsoAsUtc(isoString) {
  if (!isoString) return null;
  // Check for timezone info: +/-HH:MM or Z suffix
  // Note: Check for '-' after position 10 to avoid matching date separator (YYYY-MM-DD)
  const hasTimezone = isoString.includes('+') || isoString.includes('-', 10) || isoString.endsWith('Z');
  return parseISO(hasTimezone ? isoString : `${isoString}Z`);
}

// =============================================================================
// COMPUTED FIELD TRANSFORMATIONS (Complex logic that can't be auto-converted)
// =============================================================================

/**
 * Serialize form state → API request_type
 * Speaker is auto-derived from distribution='event'; personType drives expert vs MotS.
 */
export function serializeRequestType(formState) {
  if (formState.lookingFor === 'product') {
    return 'product';
  }

  if (formState.lookingFor === 'person') {
    if (formState.personType === 'man_on_the_street') {
      return 'man_on_the_street';
    }
    if (formState.speakerRequest) {
      return 'speaker';
    }
    return 'expert';
  }

  return null;
}

/**
 * Deserialize API request_type → form state
 * Maps API enum to lookingFor + personType.
 * speakerRequest is derived from distribution='event' in populateDerivedState.
 */
export function deserializeRequestType(apiType) {
  const result = {
    lookingFor: 'person',
    personType: 'expert',
    speakerRequest: false,
  };

  switch (apiType) {
    case 'product':
      result.lookingFor = 'product';
      break;
    case 'speaker':
      result.speakerRequest = true;
      break;
    case 'man_on_the_street':
      result.personType = 'man_on_the_street';
      break;
    case 'expert':
    case 'guest':
    case 'story_idea':
    default:
      break;
  }

  return result;
}

/**
 * Serialize form state → API request_sub_type
 * Event distribution (speaker) is handled first since eventType takes precedence.
 */
export function serializeRequestSubtype(formState) {
  if (formState.distribution === 'event') {
    if (formState.eventType === 'conference') return 'conference';
    if (formState.eventType === 'lecture') return 'lecture';
    if (formState.eventType === 'webinar') return 'webinar';
    return 'event';
  }
  if (formState.distribution === 'video') return 'video_broadcast';
  if (formState.distribution === 'radio') return 'radio';
  if (formState.distribution === 'podcast') return 'podcast';
  if (formState.distribution === 'online') return 'online_print';
  if (formState.distribution === 'other') return 'other';

  return null;
}

/**
 * Deserialize API request_sub_type → form state
 * Maps API enum back to form distribution and eventType
 */
export function deserializeRequestSubtype(apiSubtype) {
  const result = {
    distribution: 'online',
    eventType: null,
  };

  switch (apiSubtype) {
    case 'video_broadcast':
      result.distribution = 'video';
      break;
    case 'radio':
      result.distribution = 'radio';
      break;
    case 'podcast':
      result.distribution = 'podcast';
      break;
    case 'online_print':
      result.distribution = 'online';
      break;
    case 'conference':
      result.distribution = 'event';
      result.eventType = 'conference';
      break;
    case 'lecture':
      result.distribution = 'event';
      result.eventType = 'lecture';
      break;
    case 'webinar':
      result.distribution = 'event';
      result.eventType = 'webinar';
      break;
    case 'event':
      result.distribution = 'event';
      result.eventType = 'conference'; // Default event type
      break;
    case 'shared_article':
      result.distribution = 'online';
      break;
    case 'other':
      result.distribution = 'other';
      break;
    default:
      result.distribution = 'online';
      break;
  }

  return result;
}

/**
 * Serialize form logistics booleans → API logistics_details array
 * Converts individual boolean flags to array of logistics values
 */
export function serializeLogistics(formState) {
  const DISTRIBUTIONS_WITH_LOGISTICS = new Set(['radio', 'podcast', 'video', 'event']);

  if (!DISTRIBUTIONS_WITH_LOGISTICS.has(formState.distribution)) {
    return [];
  }

  return [
    formState.logisticsVideo && '+_video',
    formState.logisticsInPerson && 'in_person',
    formState.logisticsLive && 'live',
    formState.logisticsPreRecorded && 'pre_recorded',
    formState.logisticsRemote && 'remote',
  ].filter((v) => v);
}

/**
 * Deserialize API logistics_details array → form logistics booleans
 * Converts array of logistics values back to individual boolean flags
 */
export function deserializeLogistics(logisticsArray) {
  const logisticsSet = new Set(logisticsArray || []);

  return {
    logisticsVideo: logisticsSet.has('+_video'),
    logisticsInPerson: logisticsSet.has('in_person'),
    logisticsLive: logisticsSet.has('live'),
    logisticsPreRecorded: logisticsSet.has('pre_recorded'),
    logisticsRemote: logisticsSet.has('remote'),
  };
}

/**
 * Serialize form links → API links array
 * Converts single URL field to array (API expects array)
 */
export function serializeLinks(formState) {
  const DISTRIBUTIONS_WITH_LOGISTICS = new Set(['radio', 'podcast', 'video', 'event']);

  if (!DISTRIBUTIONS_WITH_LOGISTICS.has(formState.distribution)) {
    return [];
  }

  return formState.logisticsUrl ? [formState.logisticsUrl] : [];
}

/**
 * Deserialize API links array → form links string
 * Converts array back to single URL field (form uses string, not array)
 */
export function deserializeLinks(linksArray) {
  return (linksArray || [])[0] || '';
}

/**
 * Serialize form equipment_notes
 * Only include for distributions that support logistics
 */
export function serializeEquipmentNotes(formState) {
  const DISTRIBUTIONS_WITH_LOGISTICS = new Set(['radio', 'podcast', 'video', 'event']);

  return DISTRIBUTIONS_WITH_LOGISTICS.has(formState.distribution) ? formState.equipmentNotes : '';
}

/**
 * Serialize form deadline options → API deadline_at ISO string
 * Handles preset options (24hrs, 72hrs, 7days, custom) and computed deadline
 */
export function serializeDeadline(formState) {
  if (!formState.deadlineAt) {
    return null;
  }
  return formState.deadlineAt.toISOString();
}

/**
 * Serialize form schedule → API scheduled_at ISO string
 * Handles "now" (null) vs "custom" (ISO string)
 */
export function serializeSchedule(formState) {
  if (formState.schedule === 'custom' && formState.customScheduleDateTime) {
    return parse(formState.customScheduleDateTime, "yyyy-MM-dd'T'HH:mm", new Date()).toISOString();
  }
  return null;
}

/**
 * Deserialize API deadline_at → form deadline option
 * Uses heuristics to guess which preset was intended (24hrs/72hrs/7days/custom)
 * This maintains existing behavior where deadlines are reconstructed from timestamps
 *
 * @param {string} deadlineAtIso - ISO deadline timestamp
 * @param {string} scheduledAtIso - ISO scheduled publish timestamp (optional)
 * @returns {Object} { deadline, customDeadlineDateTime }
 */
export function deserializeDeadline(deadlineAtIso, scheduledAtIso = null) {
  if (!deadlineAtIso) {
    return { deadline: '7days', customDeadlineDateTime: '' };
  }

  const deadlineDate = parseIsoAsUtc(deadlineAtIso);

  // Determine base time for comparison
  // If scheduled, compare deadline to scheduled time; otherwise compare to now
  const baseTime = scheduledAtIso
    ? parseIsoAsUtc(scheduledAtIso)
    : new Date();

  const hoursDiff = (deadlineDate.getTime() - baseTime.getTime()) / (1000 * 60 * 60);

  // Heuristic matching to determine original preset
  if (Math.abs(hoursDiff - 24) < 0.5) {
    return { deadline: '24hrs', customDeadlineDateTime: '' };
  }
  if (Math.abs(hoursDiff - 72) < 0.5) {
    return { deadline: '72hrs', customDeadlineDateTime: '' };
  }
  if (Math.abs(hoursDiff - 168) < 0.5) {
    return { deadline: '7days', customDeadlineDateTime: '' };
  }

  // Custom deadline (includes old 4h/48h presets — deadline date is pre-filled so form stays editable)
  return {
    deadline: 'custom',
    customDeadlineDateTime: format(deadlineDate, "yyyy-MM-dd'T'HH:mm"),
  };
}

/**
 * Deserialize API scheduled_at → form schedule options
 * Handles "now" (null) vs "custom" (ISO string)
 */
export function deserializeSchedule(scheduledAtIso) {
  if (!scheduledAtIso) {
    return {
      schedule: 'now',
      customScheduleDateTime: '',
    };
  }

  const scheduleDate = parseIsoAsUtc(scheduledAtIso);

  return {
    schedule: 'custom',
    customScheduleDateTime: format(scheduleDate, "yyyy-MM-dd'T'HH:mm"),
  };
}

/**
 * Serialize form notification preference → API pitch_notify_mode
 */
export function serializeNotification(formState) {
  const notificationMap = { 'every-pitch': 'each', digest: 'auto_batch' };
  return notificationMap[formState.notification] || 'each';
}

/**
 * Deserialize API pitch_notify_mode → form notification preference
 */
export function deserializeNotification(pitchNotifyMode) {
  const notificationMapReverse = { each: 'every-pitch', auto_batch: 'digest' };
  return notificationMapReverse[pitchNotifyMode] || 'every-pitch';
}

// =============================================================================
// DESERIALIZATION FOR SIMPLE FIELDS
// =============================================================================

/**
 * Deserialize API response to form state
 * Handles simple field conversions (snake_case → camelCase) automatically.
 * Used in deserializeSourceRequest() as the base layer before applying
 * complex field transformations.
 */
export function deserializeSimpleFields(apiResponse) {
  return deepCamelizeKeys(apiResponse);
}
